<script>
import SaleStatusBadge from "@src/components/ui-design/badges/SaleStatusBadge.vue";
import Money from "@src/mixins/money";
import { getPaymentMethod } from "@src/helpers/payment";
import { getSaleStatusList } from "@src/helpers/sale";
import SalesService from "@services/resources/SalesService";
import ChargeRefundAction from "./ChargeRefundAction.vue";

const service = SalesService.build();

export default {
  emits: ["refund", "hidden"],
  mixins: [Money],
  components: {
    SaleStatusBadge,
    ChargeRefundAction,
  },
  props: {
    subscriptionInfo: {
      type: Object,
      default: () => ({
        period: "",
        charges: "",
        total: "",
      }),
    },
    showRefundAction: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      lastChargesRequestId: 0,
      baseFields: [
        { key: "id", label: "ID" },
        { key: "created_at", label: "Data" },
        { key: "amount", label: "Valor" },
        { key: "status", label: "Status" },
        { key: "method", label: "Método" },
      ],
      rows: [],
      selectedStatus: "",
      pagination: {
        total: 1,
        page: 1,
        perPage: 6,
        lastPage: 1,
        pageOptions: [6, 50, 100],
      },
    };
  },
  watch:{
    'pagination.page'() {
      this.getCharges();
    }
  },
  computed: {
    fields() {
      if (!this.showRefundAction) return this.baseFields;

      return [
        ...this.baseFields,
        {
          key: "action",
          label: "Ação",
          thClass: "sales-details__action-header",
          tdClass: "sales-details__action-cell",
        },
      ];
    },
  },
  methods: {
    getPaymentMethod,
    getSaleStatusList,
    async getCharges() {
      const requestId = ++this.lastChargesRequestId;

      try {
        let data = {
          page: this.pagination.page,
          url: `/${this.$route.params.id}/charges`,
          status: this.selectedStatus,
        };

        const res = await service.search(data);

        if (requestId !== this.lastChargesRequestId) return;

        const result = res.messages.data;

        this.rows = result.items;
        this.pagination.total = result.total;
        this.pagination.lastPage = result.lastPage;
      } catch (_) {
        this.$bvToast.toast("Não foi possível buscar os detalhes da venda.", {
          title: "Ops, algum erro aconteceu",
          variant: "danger",
          solid: true,
          autoHideDelay: 2000,
        });
      }
    },
    handleStatus(status) {
      this.selectedStatus = status;

      if (this.pagination.page !== 1) {
        this.pagination.page = 1;
        return;
      }

      this.getCharges();
    },
  },
  created() {
    this.getCharges();
  },
};
</script>

<template>
  <b-modal
    centered
    size="lg"
    id="charges-modal"
    title="Histórico de Cobranças"
    hide-header
    hide-footer
    @hidden="$emit('hidden')"
  >
    <div class="charges-modal">
      <div class="charges-modal__header">
        <div>
          <h6 class="sales-details__subtitle charges-modal__title">
            Histórico de Cobranças
          </h6>
          <p>Últimas cobranças relacionadas a esta venda</p>
        </div>
        <button type="button" class="charges-modal__close" aria-label="Fechar modal"
          @click="$bvModal.hide('charges-modal')">
          <span>X</span>
        </button>
      </div>
      <div class="charges-modal__filters">
        <button v-for="status in [{ id: '', name: 'Todos' }, ...getSaleStatusList()]" :key="status.id || 'all'"
          type="button" class="charges-modal__status" :aria-pressed="selectedStatus === status.id" :class="{
            'charges-modal__status--selected': selectedStatus === status.id,
          }" @click="handleStatus(status.id)">
          {{ status.name }}
        </button>
      </div>
      <div class="charges-modal__content">
        <b-table class="sales-details__table" :items="rows || []" :fields="fields" headless bordered small
          empty-text="Nenhum registro encontrado">
          <template #cell(id)="data">
            <span>#{{ data.item.id }}</span>
          </template>
          <template #cell(created_at)="data">
            <span>{{ $date(data.item.created_at) }}</span>
          </template>

          <template #cell(amount)="data">
            <div class="d-flex justify-content-start">
              R$ {{ data.item.amount === 0 ? "0,00" : formatMoney(data.item.amount) }}
            </div>
          </template>
          <template #cell(status)="data">
            <div class="d-flex justify-content-start">
              <SaleStatusBadge :status="data.item.status" />
            </div>
          </template>
          <template #cell(method)="data">
            <div class="d-flex justify-content-start">
              {{ getPaymentMethod(data.item.method) }}
            </div>
          </template>
          <template #cell(action)="data">
            <ChargeRefundAction
              :charge="data.item"
              @refund="$emit('refund', data.item)"
            />
          </template>
          <template #empty>
            <div class="text-center py-4 text-muted">
              <i class="fas fa-inbox fa-2x mb-2"></i>
              <p>Nenhum dado disponível.</p>
            </div>
          </template>
        </b-table>
        <div>
          <div class="charges-modal__bottom">
            <div>
              <p>Período</p>
              <p class="charges-modal__text">
                {{ subscriptionInfo.period }}
              </p>
            </div>
            <div>
              <p>Cobranças Pagas</p>
              <p class="charges-modal__text">
                {{ subscriptionInfo.charges }}
              </p>
            </div>
            <div>
              <p>Valor Total Pago</p>
              <p class="charges-modal__text">R$ {{ subscriptionInfo.total || "0,00" }}</p>
            </div>
          </div>
          <div class="row px-0">
            <b-col cols="12" sm="12">
              <div class="d-flex align-items-center justify-content-between">
                <div class="sales__per-page">
                  <span>Página {{ pagination.page }} de {{ pagination.lastPage }}</span>
                </div>
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <b-pagination hide-goto-end-buttons pills limit="1"
                    class="pagination-arrows-only pagination pagination-rounded mb-0" v-model="pagination.page"
                    :total-rows="pagination.total" :per-page="pagination.perPage"></b-pagination>
                </div>
              </div>
            </b-col>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss">
#charges-modal {
  .modal-content {
    border-radius: 20px;
    height: 714px;
  }

  .modal-body {
    padding: 40px;
  }
}

.charges-modal {
  .sales-details__action-header,
  .sales-details__action-cell {
    text-align: center;

    .sales-button {
      justify-content: center;
    }
  }

  height: 100%;
  width: 100%;

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 472px;
  }

  &__text {
    color: #000c0b; font-weight: 600;
  }

  &__title {
    margin-top: 0;
    margin-bottom: 0.75rem!important;
    font-size: 20px!important;
  }

  &__filters {
    overflow-y: hidden;
    overflow-x: scroll;

    display: flex;
    list-style: none;
    padding: 0;
    gap: 1rem;
    padding-bottom: 1rem;
    margin-bottom: 1rem;

    &::-webkit-scrollbar {
      height: 8px;
    }

    /* Trilha do scroll */
    &::-webkit-scrollbar-track {
      background: transparent;
    }

    /* Polegar do scroll */
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }

    /* Polegar do scroll ao passar o mouse */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  &__status {
    padding: 10px 18px;
    background: #f0f2f5;
    border-radius: 50px;
    min-width: fit-content;
    color: #000c0b;
    cursor: pointer;
    border: 0;

    &--selected {
      background: #000c0b !important;
      color: #fff;
      font-weight: 600;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    background: #f7f7f7;
    padding: 8px 20px;
    margin-bottom: 1rem;
    max-height: 76px;
    border-radius: 10px;

    p {
      margin-bottom: 0;
    }
  }

  &__close {
    background: #f0f2f5;
    padding: 4px;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    right: 20px;
    top: 20px;
    border: 0;

    span {
      color: #999e9d;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 10px;
      margin: 0px;
    }
  }

  .pagination-arrows-only {
    gap: 0.5rem;

    li:nth-child(1),
    li:nth-child(3) {
      border-radius: 10px;
      display: flex;
      align-items: center;
      width: 30px;
      height: 30px;
      text-align: center;
      font-size: 26px;
      border: 1px solid #e5e7e7;

      span {
        padding: 0px;
      }
    }

    li:nth-child(2) {
      display: none;
    }
  }

  .page-item.disabled {
    color: #999e9d;
  }

  .b-pagination-pills .page-item .page-link {
    padding: 0 !important;
    width: 100%;
    margin: 0 !important;

    &:hover,
    &:active {
      box-shadow: none;
      background: #fff;
    }
  }
}
</style>
